import type { AppDataPayload } from '../types/appData';
import { getAuthToken } from '../store';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

interface AuthorizedRequestOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | object | null;
}

interface AppDataResponse {
  payload: AppDataPayload;
  updatedAt?: string | null;
}

const serializeBody = (body: AuthorizedRequestOptions['body']): BodyInit | null | undefined => {
  if (
    body == null ||
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof URLSearchParams
  ) {
    return body as BodyInit | null | undefined;
  }
  return JSON.stringify(body);
};

const handleResponse = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'message' in payload
        ? (payload as { message: string }).message
        : response.statusText || 'Erreur serveur';
    throw new Error(message);
  }

  return payload;
};

const authorizedRequest = async <T>(
  path: string,
  options: AuthorizedRequestOptions = {}
): Promise<T> => {
  const headers = new Headers(options.headers || {});

  if (options.body !== undefined && options.body !== null && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    method: options.method ?? 'GET',
    body: serializeBody(options.body),
    credentials: 'include',
  });

  return handleResponse(response) as Promise<T>;
};

export const fetchAppData = async (): Promise<AppDataPayload> => {
  const response = await authorizedRequest<AppDataResponse>('/app-data');
  return response.payload ?? {};
};

export const updateAppData = async (payload: AppDataPayload): Promise<AppDataResponse> =>
  authorizedRequest('/app-data', {
    method: 'PUT',
    body: { payload },
  });
